import dayjs from "dayjs";
import {
	CarbonSnapFormData,
	DistanceUnit,
	NodeCodeType,
	TransportArc,
	TransportMode,
	TransportNode,
	TransportVehicleDetails,
	WeightUnit,
} from "./models";
import { DEFAULT_NODE_TYPE_IDS } from "./models";

const createDraftId = (): string => {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID();
	}
	return `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createEmptyTransportNode = (): TransportNode => ({
	id: createDraftId(),
	address: "",
	lat: null,
	lng: null,
});

export const createEmptyTransportVehicleDetails = (): TransportVehicleDetails => ({
	vehicleTypeId: "",
	engineId: "",
	returnEmptyPercent: null,
	payloadId: "",
	averageSaturationPercent: null,
	ownedVehicle: false,
});

export const createEmptyTransportArc = (fromNodeId: string, toNodeId: string): TransportArc => ({
	id: createDraftId(),
	fromNodeId,
	toNodeId,
	transportMode: TransportMode.Road,
	networkEnabled: true,
	weight: 1,
	weightUnit: WeightUnit.Tonne,
	distance: null,
	distanceUnit: DistanceUnit.Km,
	firstMileVehicle: createEmptyTransportVehicleDetails(),
	mainVehicle: createEmptyTransportVehicleDetails(),
	lastMileVehicle: createEmptyTransportVehicleDetails(),
	useExternalMainVehicle: false,
});

export const createEmptyCarbonSnapFormData = (): CarbonSnapFormData => {
	const firstNode = createEmptyTransportNode();
	const secondNode = createEmptyTransportNode();
	return {
		departureDate: dayjs(),
		numberOfTrips: 1,
		weight: 1,
		// ids come from server options, the first available one is selected in TripDataFields
		weightUnit: null,
		temperature: null,
		calculationMode: null,
		tripCode: "",
		nodes: [firstNode, secondNode],
		arcs: [createEmptyTransportArc(firstNode.id, secondNode.id)],
	};
};

export const emptyCarbonSnapFormData = createEmptyCarbonSnapFormData();

export const syncArcsWithNodes = (nodes: TransportNode[], arcs: TransportArc[]): TransportArc[] => {
	if (nodes.length < 2) return [];

	const nextArcs: TransportArc[] = [];

	for (let i = 0; i < nodes.length - 1; i += 1) {
		const fromNode = nodes[i];
		const toNode = nodes[i + 1];
		const existing = arcs.find(
			(arc) => arc.fromNodeId === fromNode.id && arc.toNodeId === toNode.id
		);
		nextArcs.push(existing ?? createEmptyTransportArc(fromNode.id, toNode.id));
	}

	return nextArcs;
};

export const resolveDefaultNodeTypeId = (
	codeType: NodeCodeType | undefined,
	nodeIndex: number,
	totalNodes: number
): number => {
	if (codeType === "iata" || codeType === "icao") return DEFAULT_NODE_TYPE_IDS.airport;
	if (codeType === "wpi") return DEFAULT_NODE_TYPE_IDS.port;

	if (nodeIndex === 0) return DEFAULT_NODE_TYPE_IDS.centralWarehouse;
	if (nodeIndex === totalNodes - 1) return DEFAULT_NODE_TYPE_IDS.customer;
	return DEFAULT_NODE_TYPE_IDS.transitPoint;
};

export const resolveNodeType = (
	codeType: NodeCodeType | undefined,
	nodeIndex: number,
	totalNodes: number
): { nodeTypeId: number; nodeTypeName: string | undefined } => {
	const nodeTypeId = resolveDefaultNodeTypeId(codeType, nodeIndex, totalNodes);
	return {
		nodeTypeId,
		nodeTypeName: undefined, // nodeTypeName will be resolved from API response
	};
};
